import { useEffect, useRef, useState } from "react";

/**
 * Number that counts up smoothly when scrolled into view.
 */
function CountUp({ to, duration = 1600, suffix = "", prefix = "", decimals = 0 }) {
  const [value, setValue] = useState(0);
  const [started, setStarted] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!ref.current || started) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setStarted(true);
          observer.disconnect();
        }
      },
      { threshold: 0.3 }
    );
    observer.observe(ref.current);
    return () => observer.disconnect();
  }, [started]);

  useEffect(() => {
    if (!started) return;
    let raf;
    const t0 = performance.now();
    const tick = (now) => {
      const t = Math.min(1, (now - t0) / duration);
      const eased = 1 - Math.pow(1 - t, 3);
      setValue(to * eased);
      if (t < 1) raf = requestAnimationFrame(tick);
      else setValue(to);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [started, to, duration]);

  return (
    <span ref={ref}>
      {prefix}
      {decimals ? value.toFixed(decimals) : Math.round(value).toLocaleString()}
      {suffix}
    </span>
  );
}

export default function StatsStrip() {
  const stats = [
    { value: 200, suffix: "+", label: "Sites running" },
    { value: 30, suffix: "k+", label: "Products restocked monthly" },
    { value: 24, suffix: "h", label: "Avg response time" },
    { value: 4.9, suffix: "★", label: "Customer rating", decimals: 1 },
  ];
  return (
    <section
      data-testid="stats-strip"
      className="relative border-y border-white/5 bg-[#0A0A0A]/80 backdrop-blur-sm"
    >
      {/* subtle green wash */}
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(254,114,0,0.06),transparent_60%)]" />
      <div className="relative mx-auto max-w-7xl px-4 md:px-8 lg:px-12 py-10 grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-8">
        {stats.map((s, i) => (
          <div
            key={s.label}
            data-testid={`stats-item-${i}`}
            className="group text-center md:text-left"
          >
            <div className="font-display text-4xl md:text-5xl font-black tracking-tight text-white">
              <CountUp to={s.value} suffix={s.suffix} decimals={s.decimals || 0} />
            </div>
            <div className="mt-1.5 text-xs sm:text-sm font-semibold uppercase tracking-[0.18em] text-zinc-400 group-hover:text-[#FE7200] transition-colors">
              {s.label}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}
